import { cloneDeep } from 'lodash'

import { AskResponse, Citation } from '../../api'

export type ParsedAnswer = {
  citations: Citation[]
  markdownFormatText: string,
  generated_chart: string | null
} | null

export const enumerateCitations = (citations: Citation[]) => {
  const filepathMap = new Map()
  for (const citation of citations) {
    const { filepath } = citation
    let part_i = 1
    if (filepathMap.has(filepath)) {
      part_i = filepathMap.get(filepath) + 1
    }
    filepathMap.set(filepath, part_i)
    citation.part_index = part_i
  }
  return citations
}

export function parseAnswer(answer: AskResponse): ParsedAnswer {
  if (typeof answer.answer !== 'string') return null
  //let answerText = answer.answer
  let answerText = answer.answer.trim() // Ensure no unwanted spaces

  // Ensure `filter_applied` is appended at the END of the answer
  if (answerText.includes('**Filter Applied:**')) {
    console.log('=== DEBUG: Filter Found  ===')
  } else if (answer.filter_applied && answer.filter_applied.trim().length > 0) {
    answerText += `\n\n**Filter Applied:** ${answer.filter_applied}`
  }

  // Collapse immediately duplicated citation blocks such as [doc2].[doc2].
  // Run repeatedly in case the model returns the same block more than twice.
  let previousText = ''
  while (previousText !== answerText) {
    previousText = answerText
    answerText = answerText
      // Single block duplicate: [doc2].[doc2]. -> [doc2].
      .replace(/(\[(?:[^\]]*?\bdoc\d{1,3}\b[^\]]*?)\])(\s*[.,;:!?]?\s*)\1/gi, '$1$2')
      // Chain duplicate: [doc1][doc3][doc4].[doc1][doc3][doc4]. -> [doc1][doc3][doc4].
      .replace(/((?:\[(?:[^\]]*?\bdoc\d{1,3}\b[^\]]*?)\]\s*)+)(\s*[.,;:!?]?\s*)\1/gi, '$1$2')
  }

  const citationsArray: Citation[] = Array.isArray(answer.citations) ? answer.citations : []

  let filteredCitations = [] as Citation[]
  let citationReindex = 0

  // Track which original doc indices we've already added (de-dupe)
  const seenDocIndices = new Set<string>()

  // Replace bracketed doc markers like:
  // [doc4], [doc4, §7.90], [doc3, §19.46; doc4, §7.91(b)]
  // with superscripts, while keeping any non-doc text (e.g., §7.90).
  answerText = answerText.replace(/\[([^\]]*?\bdoc\d{1,3}\b[^\]]*?)\]/gi, (fullMatch, inner) => {
    const docMatches = String(inner).match(/\bdoc(\d{1,3})\b/gi) ?? []

    const superscripts: string[] = []
    for (const docToken of docMatches) {
      const docNumberStr = docToken.replace(/[^0-9]/g, '')
      const docNumber = Number(docNumberStr)
      if (!Number.isFinite(docNumber) || docNumber < 1) continue

      const citation = cloneDeep(citationsArray[docNumber - 1]) as Citation
      if (!citation) continue

      const docIndexKey = docNumber.toString()
      if (!seenDocIndices.has(docIndexKey)) {
        seenDocIndices.add(docIndexKey)
        citationReindex += 1
        citation.id = docIndexKey // original doc index to de-dupe
        citation.reindex_id = citationReindex.toString() // reindex from 1 for display
        filteredCitations.push(citation)
      }

      const existing = filteredCitations.find(c => c.id === docIndexKey)
      const displayIndex = existing?.reindex_id ?? citationReindex.toString()
      superscripts.push(` ^${displayIndex}^ `)
    }

    const remainder = String(inner)
      .replace(/\bdoc\d{1,3}\b/gi, '')
      .replace(/^[\s,;:]+|[\s,;:]+$/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim()

    if (superscripts.length === 0) return fullMatch
    if (remainder.length === 0) return superscripts.join('')

    return ` (${remainder})${superscripts.join('')}`
  })

  filteredCitations = enumerateCitations(filteredCitations)
  return {
    citations: filteredCitations,
    markdownFormatText: answerText,
    generated_chart: answer.generated_chart
  }
}